import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { toast } from 'sonner';
import { Eye, EyeOff, CheckCircle2, XCircle, ArrowLeft, ArrowRight, Gift, RefreshCw, Info, Search, Users, ListChecks, Activity } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const legalForms = [
  { value: "einzelunternehmen", label: "Einzelunternehmen" },
  { value: "gbr", label: "GbR (Gesellschaft bürgerlichen Rechts)" },
  { value: "ug", label: "UG (haftungsbeschränkt)" },
  { value: "gmbh", label: "GmbH" },
  { value: "ag", label: "AG" },
  { value: "kg", label: "KG (Kommanditgesellschaft)" },
  { value: "ohg", label: "OHG (Offene Handelsgesellschaft)" }
];

const industries = [
  { value: "tankstelle", label: "Tankstelle" },
  { value: "einzelhandel", label: "Einzelhandel" },
  { value: "gastronomie", label: "Gastronomie" },
  { value: "andere", label: "Andere" }
];

// FastAPI kann detail als String, Objekt {field,errors} oder 422-Array liefern → lesbar machen
function formatApiErrorDetail(detail) {
  if (detail == null) return 'Registrierung fehlgeschlagen';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' · ');
  }
  if (detail.errors && Array.isArray(detail.errors)) return detail.errors.join(' · ');
  if (typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}

// Kompakte, durchsuchbare Markenauswahl inkl. Freie Tankstelle / Sonstige (Freitext)
function BrandSelect({ brands, colors, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [sonstige, setSonstige] = useState(() => !!value && !brands.includes(value));
  const ref = useRef(null);
  const filtered = brands.filter(b => b.toLowerCase().includes(search.toLowerCase()));

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const pick = (b) => {
    if (b === 'Sonstige') {
      setSonstige(true); onChange(''); setOpen(false);
      setTimeout(() => document.getElementById('brand-sonstige-input')?.focus(), 50);
    } else {
      setSonstige(false); onChange(b); setOpen(false); setSearch('');
    }
  };

  return (
    <div ref={ref} className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} data-testid="brand-select-trigger"
        className={`flex items-center justify-between h-12 w-full rounded-lg border px-3 text-base bg-white transition-colors ${open ? 'ring-2 ring-[#1e3a8a] border-[#1e3a8a]' : 'border-gray-300 hover:border-gray-400'} ${value ? 'text-gray-900' : 'text-gray-400'}`}>
        <span className="truncate flex items-center gap-2">
          {value && !sonstige && colors[value] && <span className="h-3 w-3 rounded-full" style={{ backgroundColor: colors[value] }} />}
          {value || 'Gesellschafter / Marke wählen…'}
        </span>
        <Search className="h-4 w-4 text-gray-400 shrink-0 ml-2" />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden">
          <div className="p-2 border-b border-gray-100">
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Marke suchen…"
              data-testid="brand-search" className="w-full h-9 rounded-lg border border-gray-200 px-3 text-sm focus:outline-none focus:border-[#1e3a8a]" />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-4 text-sm text-gray-400 text-center">Keine Treffer</div>
            ) : filtered.map(b => (
              <button key={b} type="button" onClick={() => pick(b)} data-testid={`brand-${b}`}
                className={`w-full flex items-center justify-between px-3 py-2.5 text-sm text-left hover:bg-gray-50 ${b === value ? 'bg-[#1e3a8a]/5 text-[#1e3a8a] font-medium' : 'text-gray-800'} ${b === 'Sonstige' ? 'border-t border-gray-100 text-violet-600 font-medium' : ''}`}>
                <span className="flex items-center gap-2">
                  {colors[b] && <span className="h-3 w-3 rounded-full" style={{ backgroundColor: colors[b] }} />}
                  {b}
                </span>
                {b === value && <CheckCircle2 className="h-4 w-4 text-[#1e3a8a]" />}
              </button>
            ))}
          </div>
        </div>
      )}
      {sonstige && (
        <div className="mt-2">
          <input id="brand-sonstige-input" type="text" value={value} maxLength={50}
            onChange={e => onChange(e.target.value)} placeholder="Markenname eingeben (z.B. Famila, Picard, …)"
            data-testid="brand-sonstige-input"
            className="w-full h-12 border border-gray-300 rounded-lg px-3 text-base bg-white focus:outline-none focus:ring-2 focus:ring-[#1e3a8a]" />
          <p className="text-xs text-gray-500 mt-1">Freier Markenname – wird Ihrem Betrieb zugeordnet.</p>
        </div>
      )}
      {value && (
        <p className="text-xs text-green-600 mt-1.5 font-medium flex items-center gap-1" data-testid="brand-selected-hint">
          <CheckCircle2 className="h-3 w-3" /> {value} ausgewählt
        </p>
      )}
    </div>
  );
}

export const Register = () => {
  const [step, setStep] = useState(1); // 1 Zugang · 2 Unternehmen · 3 Standort
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirm, setShowPasswordConfirm] = useState(false);
  const [showPwInfo, setShowPwInfo] = useState(false);
  const [pwFocused, setPwFocused] = useState(false);
  const [showReferral, setShowReferral] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { register } = useAuth();
  const [searchParams] = useSearchParams();

  const urlReferralCode = searchParams.get('ref');
  const [referralCodeFromUrl] = useState(urlReferralCode ? urlReferralCode.trim().toUpperCase() : '');
  const [invalidCodeWarning, setInvalidCodeWarning] = useState('');
  const [showReactivatePopup, setShowReactivatePopup] = useState(false);

  const [formData, setFormData] = useState({
    first_name: '', last_name: '', company_name: '', legal_form: '',
    industry: 'tankstelle', brand: '', email: '', password: '', password_confirm: '',
    referral_code: urlReferralCode ? urlReferralCode.trim().toUpperCase() : '',
    location_name: '', station_number: '', street: '', house_number: '',
    postal_code: '', city: '', address_search: '', legal_accepted: false,
  });

  const [passwordValidation, setPasswordValidation] = useState({
    length: false, uppercase: false, lowercase: false, number: false, special: false
  });

  const DEFAULT_BRANDS = [
    'JET','Shell','Aral','Esso','TotalEnergies','OMV','AVIA',
    'Agip/Eni','HEM','Star/ORLEN','Raiffeisen','Westfalen',
    'bft','Freie Tankstelle','Sonstige'
  ];
  const [availableBrands, setAvailableBrands] = useState(DEFAULT_BRANDS);
  const [brandColors, setBrandColors] = useState({});

  useEffect(() => {
    if (referralCodeFromUrl) setShowReferral(true);
  }, [referralCodeFromUrl]);

  useEffect(() => {
    axios.get(`${API}/api/brands?industry=tankstelle`)
      .then(res => {
        if (res.data && res.data.length > 0) {
          const apiNames = res.data.map(b => b.name);
          const merged = [...new Set([
            ...DEFAULT_BRANDS.filter(b => b !== 'Sonstige'),
            ...apiNames.filter(b => b !== 'Sonstige'),
            'Sonstige'
          ])];
          setAvailableBrands(merged);
          const colors = {};
          res.data.forEach(b => { if (b.color) colors[b.name] = b.color; });
          setBrandColors(colors);
        }
      })
      .catch(() => {});
  }, []); // eslint-disable-line

  useEffect(() => {
    const pwd = formData.password;
    setPasswordValidation({
      length: pwd.length >= 8,
      uppercase: /[A-Z]/.test(pwd),
      lowercase: /[a-z]/.test(pwd),
      number: /[0-9]/.test(pwd),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(pwd)
    });
  }, [formData.password]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  const handleEmailBlur = async (e) => {
    const emailVal = e.target.value.trim().toLowerCase();
    if (!emailVal || !emailVal.includes('@')) return;
    try {
      const res = await axios.post(`${API}/api/auth/check-email`, { email: emailVal });
      const status = res.data.status;
      if (status === 'cancelled' || status === 'active' || status === 'trialing') setShowReactivatePopup(true);
    } catch { /* nicht blockieren */ }
  };

  const handleAddressSelect = (addressData) => {
    const city = addressData.city || '';
    const brand = formData.brand || formData.industry || '';
    const suggestedName = formData.location_name.trim() === ''
      ? [brand, city].filter(Boolean).join(' ')
      : formData.location_name;
    setFormData(prev => ({
      ...prev,
      street: addressData.street || '',
      house_number: addressData.house_number || '',
      postal_code: addressData.postal_code || '',
      city,
      location_name: suggestedName,
    }));
  };

  const pwScore = [passwordValidation.length, passwordValidation.uppercase, passwordValidation.lowercase, passwordValidation.number, passwordValidation.special].filter(Boolean).length;
  // Hinweis: Dies spiegelt NUR die Erfüllung der Zeichenregeln wider – KEINE Aussage über echte Passwortstärke.
  const meter = pwScore === 5
    ? { color: 'bg-green-600', w: '100%', text: 'text-green-600', label: 'Anforderungen erfüllt' }
    : pwScore === 0
      ? { color: 'bg-gray-300', w: '8%', text: 'text-gray-500', label: 'Anforderungen noch nicht erfüllt' }
      : { color: 'bg-amber-500', w: `${Math.max(pwScore, 1) * 20}%`, text: 'text-amber-600', label: 'Anforderungen noch nicht erfüllt' };

  const validateZugang = () => {
    if (!formData.email.trim()) { toast.error('Bitte E-Mail eingeben'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) { toast.error('Bitte gültige E-Mail eingeben'); return false; }
    if (!Object.values(passwordValidation).every(Boolean)) { toast.error('Passwort erfüllt nicht alle Anforderungen'); return false; }
    if (formData.password !== formData.password_confirm) { toast.error('Passwörter stimmen nicht überein'); return false; }
    return true;
  };

  const validateUnternehmen = () => {
    if (!formData.first_name.trim()) { toast.error('Bitte Vornamen eingeben'); return false; }
    if (!formData.last_name.trim()) { toast.error('Bitte Nachnamen eingeben'); return false; }
    if (!formData.company_name.trim()) { toast.error('Bitte Firmennamen eingeben'); return false; }
    if (!formData.legal_form) { toast.error('Bitte Rechtsform auswählen'); return false; }
    if (formData.industry === 'tankstelle' && !formData.brand) { toast.error('Bitte Gesellschafter / Marke auswählen'); return false; }
    return true;
  };

  const validateStandort = () => {
    if (!formData.location_name.trim()) { toast.error('Bitte Standortnamen eingeben'); return false; }
    if (!formData.street.trim()) { toast.error('Bitte Straße eingeben'); return false; }
    if (!formData.house_number.trim()) { toast.error('Bitte Hausnummer eingeben'); return false; }
    if (!formData.postal_code.trim()) { toast.error('Bitte PLZ eingeben'); return false; }
    if (!formData.city.trim()) { toast.error('Bitte Stadt eingeben'); return false; }
    if (!formData.legal_accepted) { toast.error('Bitte AGB und Datenschutzerklärung akzeptieren'); return false; }
    return true;
  };

  const handleNext = () => {
    if (step === 1 && validateZugang()) setStep(2);
    else if (step === 2 && validateUnternehmen()) setStep(3);
  };
  const handleBack = () => setStep(prev => Math.max(1, prev - 1));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return; // Doppelklick-/Mehrfach-Submit-Schutz
    if (!validateStandort()) return;
    setLoading(true);
    try {
      const registrationData = {
        email: formData.email.toLowerCase().trim(),
        password: formData.password,
        name: `${formData.first_name} ${formData.last_name}`,
        first_name: formData.first_name.trim(),
        last_name: formData.last_name.trim(),
        company_name: formData.company_name.trim(),
        legal_form: formData.legal_form,
        industry: formData.industry,
        brand: formData.brand || null,
        referral_code: formData.referral_code.trim().toUpperCase() || null,
        deal_coupon: false,
        location_name: formData.location_name.trim(),
        station_number: formData.station_number.trim() || null,
        street: formData.street.trim(),
        house_number: formData.house_number.trim(),
        postal_code: formData.postal_code.trim(),
        city: formData.city.trim(),
        location_count: 1,
        legal_accepted: formData.legal_accepted,
        legal_agb_version: '1.0',
        legal_privacy_version: '1.0',
      };
      const response = await register(registrationData);
      if (response?.email_send_failed) {
        // Wahrheitsgemäß: Konto angelegt, aber Versand fehlgeschlagen → Bestätigung ausstehend
        toast.warning('Konto angelegt – die Bestätigungs-E-Mail konnte nicht gesendet werden. Bitte nutzen Sie auf der nächsten Seite „E-Mail erneut senden".', { duration: 10000 });
      } else if (response?.referral_code_invalid) {
        // Dauerhaft sichtbare Warnung, da direkt danach zur Paketwahl navigiert wird
        toast.warning('Empfehlungscode nicht gefunden – Registrierung wurde trotzdem abgeschlossen.', { duration: 8000 });
      } else {
        toast.success('Konto erstellt! Bitte bestätigen Sie Ihre E-Mail-Adresse.');
      }
      navigate('/admin/subscription?onboarding=true', {
        state: { referralInvalid: !!response?.referral_code_invalid }
      });
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail) || 'Registrierung fehlgeschlagen');
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Zugang', 'Unternehmen', 'Standort'];

  const pwRequirements = [
    { ok: passwordValidation.length, label: 'Mindestens 8 Zeichen' },
    { ok: passwordValidation.uppercase, label: 'Ein Großbuchstabe (A–Z)' },
    { ok: passwordValidation.lowercase, label: 'Ein Kleinbuchstabe (a–z)' },
    { ok: passwordValidation.number, label: 'Eine Ziffer (0–9)' },
    { ok: passwordValidation.special, label: 'Ein Sonderzeichen (!?@#…)' },
  ];

  return (
    <div className="min-h-screen flex bg-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* Reaktivierungs-Popup */}
      {showReactivatePopup && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4" data-testid="reactivate-popup">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full text-center">
            <div className="flex justify-center mb-4"><RefreshCw className="w-12 h-12 text-[#1e3a8a]" /></div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Schön, Sie wiederzusehen!</h2>
            <p className="text-gray-600 mb-6">Wir haben Ihr Profil noch gespeichert – alle Daten, Produkte und Einstellungen sind noch da. Möchten Sie Ihren Account reaktivieren?</p>
            <div className="flex flex-col gap-3">
              <Button onClick={() => {
                  const ref = referralCodeFromUrl || formData.referral_code;
                  const url = ref ? `/reactivate?ref=${ref}&email=${encodeURIComponent(formData.email)}` : `/reactivate?email=${encodeURIComponent(formData.email)}`;
                  navigate(url);
                }} className="bg-[#1e3a8a] hover:bg-[#1e40af] text-white font-bold py-3" data-testid="reactivate-yes-btn">
                Ja, Account reaktivieren
              </Button>
              <Button variant="outline" onClick={() => setShowReactivatePopup(false)} className="py-3" data-testid="reactivate-no-btn">
                Nein, andere E-Mail verwenden
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* LINKE MARKENFLÄCHE (Desktop ~40%) */}
      <div className="hidden lg:flex lg:w-[40%] flex-col justify-between p-12 text-white relative overflow-hidden" style={{ background: '#111a33' }}>
        <div className="relative z-10">
          <img src="/allflowx-horizontal-dark.png" alt="AllFlowX" className="h-9 w-auto" />
        </div>
        <div className="relative z-10 max-w-md">
          <h1 className="text-4xl font-bold leading-tight mb-5">Ihr Betrieb.<br />Ein Überblick.</h1>
          <p className="text-blue-100/80 text-base leading-relaxed mb-10">
            AllFlowX bündelt Personal, Aufgaben und Betriebsabläufe Ihrer Station an einem Ort – digital, nachvollziehbar und jederzeit griffbereit.
          </p>
          <ul className="space-y-5">
            {[
              { icon: Users, t: 'Personal', d: 'Mitarbeiter, Verträge und Unterlagen zentral verwalten.' },
              { icon: ListChecks, t: 'Aufgaben', d: 'Checklisten und Unterweisungen zuverlässig erledigen.' },
              { icon: Activity, t: 'Betriebsabläufe', d: 'MHD, Temperatur und Bestellungen im Griff.' },
            ].map(({ icon: Icon, t, d }) => (
              <li key={t} className="flex items-start gap-3">
                <span className="mt-0.5 flex h-9 w-9 items-center justify-center rounded-lg bg-white/10 shrink-0"><Icon className="h-5 w-5 text-blue-200" /></span>
                <span><span className="block font-semibold">{t}</span><span className="block text-sm text-blue-100/70">{d}</span></span>
              </li>
            ))}
          </ul>
        </div>
        <div className="relative z-10 text-xs text-blue-100/50">© {new Date().getFullYear()} AllFlowX</div>
      </div>

      {/* RECHTES FORMULAR (~60%) */}
      <div className="flex-1 flex flex-col items-center overflow-y-auto">
        <div className="w-full max-w-xl px-6 py-10 sm:px-10">
          {/* Mobiles Logo */}
          <div className="lg:hidden mb-8 flex justify-center">
            <img src="/allflowx-horizontal-light.png" alt="AllFlowX" className="h-9 w-auto" />
          </div>

          {/* Stepper */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              {steps.map((label, i) => {
                const n = i + 1;
                const active = step === n, done = step > n;
                return (
                  <React.Fragment key={label}>
                    <div className="flex items-center gap-2" data-testid={`stepper-${n}`}>
                      <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-semibold transition-colors ${done ? 'bg-green-600 text-white' : active ? 'bg-[#1e3a8a] text-white' : 'bg-gray-100 text-gray-400'}`}>
                        {done ? <CheckCircle2 className="h-4 w-4" /> : n}
                      </span>
                      <span className={`text-sm font-medium ${active ? 'text-gray-900 inline' : 'text-gray-400 hidden sm:inline'}`}>{label}</span>
                    </div>
                    {n < steps.length && <span className={`flex-1 h-px mx-2 ${step > n ? 'bg-green-600' : 'bg-gray-200'}`} />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* ─── SCHRITT 1: ZUGANG ─── */}
            {step === 1 && (
              <div className="space-y-5" data-testid="step-zugang">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Konto anlegen</h2>
                  <p className="text-gray-500 text-sm mt-1">Ihre E-Mail ist zugleich Ihr Anmeldename.</p>
                </div>
                <div>
                  <Label htmlFor="email" className="text-sm">E-Mail-Adresse *</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} onBlur={handleEmailBlur}
                    placeholder="ihre.email@beispiel.de" className="h-12 text-base mt-1" required data-testid="register-email-input" />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <Label htmlFor="password" className="text-sm">Passwort *</Label>
                    <button type="button" aria-label="Passwort-Anforderungen anzeigen" aria-expanded={showPwInfo}
                      onClick={() => setShowPwInfo(v => !v)} className="text-gray-400 hover:text-[#1e3a8a]" data-testid="pw-info-btn">
                      <Info className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="relative mt-1">
                    <Input id="password" name="password" type={showPassword ? 'text' : 'password'} value={formData.password}
                      onChange={handleChange} onFocus={() => setPwFocused(true)} onBlur={() => setPwFocused(false)}
                      placeholder="••••••••" className="h-12 text-base pr-11" required data-testid="register-password-input" />
                    <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label="Passwort anzeigen"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {/* Stärkebalken + Text (Farbe nicht alleiniger Indikator) */}
                  {formData.password && (
                    <div className="mt-2" data-testid="pw-strength">
                      <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                        <div className={`h-full ${meter.color} transition-all`} style={{ width: meter.w }} />
                      </div>
                      <p className={`text-xs mt-1 font-medium ${meter.text}`} data-testid="pw-meter-label">{meter.label}</p>
                    </div>
                  )}
                  {/* Anforderungen: per Klick (Info) oder Fokus zugänglich, keine permanente Dauerliste */}
                  {(showPwInfo || pwFocused) && (
                    <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1" data-testid="pw-requirements">
                      {pwRequirements.map(r => (
                        <div key={r.label} className={`flex items-center gap-1.5 text-xs ${r.ok ? 'text-green-600' : 'text-gray-500'}`}>
                          {r.ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5 text-gray-300" />}
                          {r.label}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div>
                  <Label htmlFor="password_confirm" className="text-sm">Passwort wiederholen *</Label>
                  <div className="relative mt-1">
                    <Input id="password_confirm" name="password_confirm" type={showPasswordConfirm ? 'text' : 'password'}
                      value={formData.password_confirm} onChange={handleChange} placeholder="••••••••" className="h-12 text-base pr-11" required
                      data-testid="register-password-confirm-input" />
                    <button type="button" onClick={() => setShowPasswordConfirm(!showPasswordConfirm)} aria-label="Passwort anzeigen"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                      {showPasswordConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {formData.password_confirm && formData.password !== formData.password_confirm && (
                    <p className="text-xs text-red-600 mt-1">Passwörter stimmen nicht überein</p>
                  )}
                </div>
                <Button type="button" onClick={handleNext} className="w-full h-12 text-base bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="next-btn">
                  Weiter <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </div>
            )}

            {/* ─── SCHRITT 2: UNTERNEHMEN ─── */}
            {step === 2 && (
              <div className="space-y-5" data-testid="step-unternehmen">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Ihr Unternehmen</h2>
                  <p className="text-gray-500 text-sm mt-1">Diese Angaben erscheinen auf Verträgen und Belegen.</p>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="first_name" className="text-sm">Vorname *</Label>
                    <Input id="first_name" name="first_name" value={formData.first_name} onChange={handleChange} placeholder="Max" className="h-12 text-base mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="last_name" className="text-sm">Nachname *</Label>
                    <Input id="last_name" name="last_name" value={formData.last_name} onChange={handleChange} placeholder="Mustermann" className="h-12 text-base mt-1" />
                  </div>
                </div>
                <div>
                  <Label htmlFor="company_name" className="text-sm">Firmenname *</Label>
                  <Input id="company_name" name="company_name" value={formData.company_name} onChange={handleChange} placeholder="Mustermann Tankstellen GmbH" className="h-12 text-base mt-1" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm">Rechtsform *</Label>
                    <Select value={formData.legal_form} onValueChange={(v) => handleSelectChange('legal_form', v)}>
                      <SelectTrigger className="h-12 text-base mt-1" data-testid="legal-form-select"><SelectValue placeholder="Bitte wählen" /></SelectTrigger>
                      <SelectContent>{legalForms.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm">Branche *</Label>
                    <Select value={formData.industry} onValueChange={(v) => handleSelectChange('industry', v)}>
                      <SelectTrigger className="h-12 text-base mt-1" data-testid="industry-select"><SelectValue /></SelectTrigger>
                      <SelectContent>{industries.map(i => <SelectItem key={i.value} value={i.value}>{i.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>
                {formData.industry === 'tankstelle' && (
                  <div>
                    <Label className="text-sm">Gesellschafter / Marke *</Label>
                    <div className="mt-1">
                      <BrandSelect brands={availableBrands} colors={brandColors} value={formData.brand} onChange={(v) => handleSelectChange('brand', v)} />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Sie erhalten automatisch die passenden Produktvorschläge für Ihre Marke.</p>
                  </div>
                )}

                {/* Empfehlungscode optional, kompakt aufklappbar */}
                <div>
                  {!showReferral ? (
                    <button type="button" onClick={() => setShowReferral(true)} className="text-sm text-[#1e3a8a] hover:underline flex items-center gap-1.5" data-testid="referral-toggle">
                      <Gift className="h-4 w-4" /> Empfehlungscode? (optional)
                    </button>
                  ) : (
                    <div>
                      <Label htmlFor="referral_code" className="text-sm flex items-center gap-1.5"><Gift className="h-4 w-4 text-[#1e3a8a]" /> Empfehlungscode (optional)</Label>
                      <Input id="referral_code" name="referral_code" value={formData.referral_code}
                        onChange={(e) => { setFormData(prev => ({ ...prev, referral_code: e.target.value.trim().toUpperCase() })); setInvalidCodeWarning(''); }}
                        placeholder="z. B. ABCD0123" disabled={!!referralCodeFromUrl}
                        className={`h-12 text-base mt-1 ${referralCodeFromUrl ? 'bg-gray-100 cursor-not-allowed' : ''}`} data-testid="referral-input" />
                      {referralCodeFromUrl && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> Aus Empfehlungslink übernommen (wird beim Abschluss geprüft)</p>
                      )}
                      {invalidCodeWarning && <p className="text-xs text-orange-600 mt-1">{invalidCodeWarning}</p>}
                    </div>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="button" onClick={handleBack} variant="outline" className="w-1/3 h-12"><ArrowLeft className="mr-1 h-4 w-4" /> Zurück</Button>
                  <Button type="button" onClick={handleNext} className="w-2/3 h-12 text-base bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="next-btn">Weiter <ArrowRight className="ml-2 h-4 w-4" /></Button>
                </div>
              </div>
            )}

            {/* ─── SCHRITT 3: STANDORT ─── */}
            {step === 3 && (
              <div className="space-y-5" data-testid="step-standort">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Ihr erster Standort</h2>
                  <p className="text-gray-500 text-sm mt-1">Adresse suchen – der Standortname wird vorgeschlagen.</p>
                </div>
                <div>
                  <Label className="text-sm">Adresse suchen</Label>
                  <div className="mt-1">
                    <AddressAutocomplete value={formData.address_search}
                      onChange={(value) => setFormData(prev => ({ ...prev, address_search: value }))}
                      onAddressSelect={handleAddressSelect} placeholder="Suchen Sie Ihre Adresse..." />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div className="sm:col-span-2">
                    <Label htmlFor="street" className="text-sm">Straße *</Label>
                    <Input id="street" name="street" value={formData.street} onChange={handleChange} placeholder="Hauptstraße" className="h-12 text-base mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="house_number" className="text-sm">Nr. *</Label>
                    <Input id="house_number" name="house_number" value={formData.house_number} onChange={handleChange} placeholder="1" className="h-12 text-base mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="postal_code" className="text-sm">PLZ *</Label>
                    <Input id="postal_code" name="postal_code" value={formData.postal_code} onChange={handleChange} placeholder="12345" className="h-12 text-base mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="city" className="text-sm">Stadt *</Label>
                    <Input id="city" name="city" value={formData.city} onChange={handleChange} placeholder="Berlin" className="h-12 text-base mt-1" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="location_name" className="text-sm">Standortname *</Label>
                    <Input id="location_name" name="location_name" value={formData.location_name} onChange={handleChange} placeholder="z.B. JET Köln" className="h-12 text-base mt-1" />
                  </div>
                  <div>
                    <Label htmlFor="station_number" className="text-sm">Stationsnummer (optional)</Label>
                    <Input id="station_number" name="station_number" value={formData.station_number} onChange={handleChange} placeholder="z.B. 5779" className="h-12 text-base mt-1" />
                  </div>
                </div>

                <div className="border border-blue-200 bg-blue-50 rounded-lg p-4">
                  <label htmlFor="legal_accepted" className="flex items-start gap-3 cursor-pointer">
                    <input type="checkbox" id="legal_accepted" checked={formData.legal_accepted}
                      onChange={(e) => setFormData(prev => ({ ...prev, legal_accepted: e.target.checked }))}
                      className="mt-1 w-4 h-4 rounded border-gray-300 text-[#1e3a8a] focus:ring-[#1e3a8a]" data-testid="legal-checkbox" />
                    <span className="text-sm text-gray-700">
                      Ich akzeptiere die{' '}
                      <Link to="/agb" target="_blank" className="text-[#1e3a8a] hover:underline font-medium">AGB</Link>{' '}und die{' '}
                      <Link to="/datenschutz" target="_blank" className="text-[#1e3a8a] hover:underline font-medium">Datenschutzerklärung</Link> *
                    </span>
                  </label>
                </div>

                <div className="flex gap-3">
                  <Button type="button" onClick={handleBack} variant="outline" className="w-1/3 h-12"><ArrowLeft className="mr-1 h-4 w-4" /> Zurück</Button>
                  <Button type="submit" disabled={loading || !formData.legal_accepted} className="w-2/3 h-12 text-base bg-[#1e3a8a] hover:bg-[#1e40af]" data-testid="submit-btn">
                    {loading ? 'Konto wird angelegt…' : 'Konto erstellen'}
                  </Button>
                </div>
              </div>
            )}
          </form>

          {/* Ein Login-Link */}
          <p className="text-center text-sm text-gray-500 mt-6" data-testid="login-link-row">
            Bereits registriert?{' '}
            <Link to="/login" className="text-[#1e3a8a] hover:underline font-medium">Jetzt anmelden</Link>
          </p>
          <div className="mt-4 flex items-center justify-center gap-4 text-xs text-gray-400">
            <Link to="/impressum" className="hover:text-gray-700">Impressum</Link>
            <span>•</span>
            <Link to="/datenschutz" className="hover:text-gray-700">Datenschutz</Link>
            <span>•</span>
            <Link to="/agb" className="hover:text-gray-700">AGB</Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
